import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Lock, Pencil, Plus, Trash2 } from "lucide-react";

const TYPES = ["int", "float", "bool", "string", "date", "enum", "flags", "json", "arc", "group"];
const WIDGETS = ["", "slider", "segmented", "zoneGrid", "json"];
const LABEL = "text-xs uppercase tracking-[0.15em] text-slate-600";
const NUM = (v) => (v === "" || v === undefined || v === null ? undefined : Number(v));

function Chip({ children }) {
  return <Badge variant="outline" className="font-mono text-[10px]">{children}</Badge>;
}

// Add / edit / delete / reorder field definitions. Core fields (read by the Unity app) are locked:
// they can be relabelled, hidden, re-ranged and re-defaulted, but not deleted, renamed or retyped.
export default function FieldListEditor({ fields, onChange, allowGroups = true, allowArc = true, domains = [], moduleInputs = false, depth = 0 }) {
  const [editing, setEditing] = useState(null);     // {index|-1, field}
  const [open, setOpen] = useState({});

  const move = (i, d) => {
    const next = [...fields]; const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(fields.filter((_, k) => k !== i));
  const save = (field) => {
    const next = [...fields];
    if (editing.index === -1) next.push(field); else next[editing.index] = field;
    onChange(next);
    setEditing(null);
  };

  return (
    <div className={depth ? "ml-5 border-l border-purple-100 pl-3" : ""}>
      {fields.map((f, i) => (
        <div key={`${f.key}-${i}`}>
          <div className="flex items-center gap-2 py-1.5 border-b border-slate-100" data-testid={`field-row-${f.key}`}>
            {f.type === "group" ? (
              <button type="button" onClick={() => setOpen({ ...open, [f.key]: !open[f.key] })}>
                {open[f.key] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
            ) : <span className="w-4" />}
            <span className="font-mono text-sm font-medium">{f.key}</span>
            <span className="text-sm text-slate-500 truncate">{f.label}</span>
            <Chip>{f.type}</Chip>
            {f.core ? <Lock className="h-3 w-3 text-slate-400" title="Read by the Unity app" /> : <Chip>custom</Chip>}
            {(f.hiddenFrom || []).length > 0 && <Chip>hidden</Chip>}
            {(f.readOnlyFor || []).length > 0 && <Chip>read-only</Chip>}
            <span className="flex-1" />
            <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === fields.length - 1}><ArrowDown className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing({ index: i, field: f })} data-testid={`edit-field-${f.key}`}><Pencil className="h-4 w-4" /></Button>
            <Button variant="ghost" size="sm" disabled={!!f.core} title={f.core ? "Core fields cannot be deleted" : "Delete"}
              onClick={() => remove(i)} data-testid={`delete-field-${f.key}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
          {f.type === "group" && open[f.key] && (
            <FieldListEditor fields={f.fields || []} depth={depth + 1} allowGroups={false} allowArc={allowArc} domains={domains}
              moduleInputs={moduleInputs}
              onChange={(sub) => { const next = [...fields]; next[i] = { ...f, fields: sub }; onChange(next); }} />
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" className="mt-2" data-testid={`add-field-${depth}`}
        onClick={() => setEditing({ index: -1, field: { key: "", label: "", type: "int", core: false } })}>
        <Plus className="h-4 w-4 mr-1" /> Add {depth === 0 && allowGroups ? "field or group" : "field"}
      </Button>
      {editing && (
        <FieldDialog initial={editing.field} isNew={editing.index === -1} onClose={() => setEditing(null)} onSave={save}
          types={TYPES.filter((t) => (allowGroups || t !== "group") && (allowArc || t !== "arc"))}
          domains={domains} moduleInputs={moduleInputs} />
      )}
    </div>
  );
}

function FieldDialog({ initial, isNew, onClose, onSave, types, domains, moduleInputs }) {
  const [f, setF] = useState({ ...initial, optionsText: (initial.options || []).map((o) => (typeof o === "object" ? o.value : o)).join(", ") });
  const core = !!initial.core && !isNew;
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleRole = (list, on) => {
    const cur = new Set(f[list] || []);
    on ? cur.add("therapist") : (cur.delete("therapist"), cur.delete("doctor"));
    set(list, [...cur]);
  };
  const numeric = f.type === "int" || f.type === "float";

  const submit = () => {
    const out = { ...f };
    delete out.optionsText;
    out.core = isNew ? false : !!initial.core;
    if (numeric) {
      out.min = NUM(f.min); out.max = NUM(f.max);
      if (out.default !== undefined && out.default !== "") out.default = Number(out.default); else delete out.default;
    }
    if (f.type === "enum" || f.type === "flags") {
      const list = f.optionsText.split(",").map((x) => x.trim()).filter(Boolean);
      out.options = list;
      if (!list.length) delete out.options;
    } else delete out.options;
    ["min", "max", "unit", "widget", "optionsFrom", "panel", "onlyIfDomain", "fills", "label"].forEach((k) => {
      if (out[k] === "" || out[k] === undefined) delete out[k];
    });
    if (f.type === "bool" && out.default !== undefined) out.default = !!out.default;
    if (out.default === "" || out.default === undefined) delete out.default;
    if (out.type !== "group") delete out.fields;
    onSave(out);
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[88vh] overflow-y-auto" data-testid="field-dialog">
        <DialogHeader><DialogTitle className="font-display">{isNew ? "New field" : `Edit ${initial.key}`}</DialogTitle></DialogHeader>
        {core && <div className="text-xs rounded-md bg-amber-50 border border-amber-200 text-amber-900 p-2">
          <Lock className="inline h-3 w-3 mr-1" />The Unity app reads this field by name. You can relabel, hide, re-range and re-default it, but not rename, retype or delete it.</div>}
        <div className="grid grid-cols-2 gap-4">
          <div><Label className={LABEL}>Key</Label>
            <Input value={f.key} disabled={core} onChange={(e) => set("key", e.target.value)} className="font-mono" data-testid="fd-key" />
            <div className="text-[10px] text-slate-400 mt-1">Capital first letter, letters/digits only (Unity reads it exactly)</div></div>
          <div><Label className={LABEL}>Label</Label><Input value={f.label || ""} onChange={(e) => set("label", e.target.value)} data-testid="fd-label" /></div>
          <div><Label className={LABEL}>Type</Label>
            <Select value={f.type} disabled={core} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="fd-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select></div>
          {f.type !== "group" && f.type !== "arc" && (
            <div><Label className={LABEL}>Default</Label>
              {f.type === "bool"
                ? <div className="pt-2"><Switch checked={!!f.default} onCheckedChange={(v) => set("default", v)} /></div>
                : <Input value={f.default ?? ""} onChange={(e) => set("default", e.target.value)} data-testid="fd-default" />}</div>
          )}
          {numeric && (<>
            <div><Label className={LABEL}>Min</Label><Input type="number" value={f.min ?? ""} onChange={(e) => set("min", e.target.value)} data-testid="fd-min" /></div>
            <div><Label className={LABEL}>Max</Label><Input type="number" value={f.max ?? ""} onChange={(e) => set("max", e.target.value)} data-testid="fd-max" /></div>
          </>)}
          {f.type !== "group" && f.type !== "arc" && f.type !== "bool" && (
            <div><Label className={LABEL}>Unit</Label><Input value={f.unit || ""} onChange={(e) => set("unit", e.target.value)} placeholder="m, deg, %…" /></div>
          )}
          {(f.type === "enum" || f.type === "flags") && (<>
            <div className="col-span-2"><Label className={LABEL}>Options (comma separated)</Label>
              <Input value={f.optionsText} onChange={(e) => set("optionsText", e.target.value)} data-testid="fd-options" /></div>
            {f.type === "flags" && <div className="col-span-2"><Label className={LABEL}>…or options from</Label>
              <Input value={f.optionsFrom || ""} onChange={(e) => set("optionsFrom", e.target.value)} placeholder="reachZones.cells" className="font-mono" /></div>}
          </>)}
          {f.type !== "group" && f.type !== "arc" && (
            <div><Label className={LABEL}>Widget</Label>
              <Select value={f.widget || "default"} onValueChange={(v) => set("widget", v === "default" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{WIDGETS.map((w) => <SelectItem key={w || "default"} value={w || "default"}>{w || "default"}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          {f.type === "group" && (
            <div><Label className={LABEL}>Panel</Label>
              <Select value={f.panel || "default"} onValueChange={(v) => set("panel", v === "default" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["default", "difficultyRules", "reach", "patientFields"].map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          {moduleInputs && (
            <div><Label className={LABEL}>Only if module has domain</Label>
              <Select value={f.onlyIfDomain || "any"} onValueChange={(v) => set("onlyIfDomain", v === "any" ? "" : v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="any">any module</SelectItem>{domains.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <div className="col-span-2 grid grid-cols-2 gap-4 border-t border-slate-100 pt-3">
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-2">
              <span className="text-sm">Hidden from therapists</span>
              <Switch checked={(f.hiddenFrom || []).length > 0} onCheckedChange={(v) => toggleRole("hiddenFrom", v)} data-testid="fd-hidden" />
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-2">
              <span className="text-sm">Read-only for therapists</span>
              <Switch checked={(f.readOnlyFor || []).length > 0} onCheckedChange={(v) => toggleRole("readOnlyFor", v)} data-testid="fd-readonly" />
            </div>
            <div className="col-span-2 text-[11px] text-slate-400">Admins always see and edit everything. The headset always receives every field.</div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="btn-brand rounded-full" onClick={submit} disabled={!(f.label || "").trim() || !(f.key || "").trim()} data-testid="fd-save">{isNew ? "Add" : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
